import { useState, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { UploadIcon } from "@/assets/icons/upload";
import { CheckLineIcon } from "@/assets/icons/check-line";
import { RetryIcon } from "@/assets/icons/retry";
import { useInfluencerStore } from "@/features/influencer-monitoring/store/influencerStore";

type DialogState = "upload" | "progress" | "success" | "error";

interface ImportDialogProps {
  trigger?: React.ReactNode;
  onUpload?: (file: File) => void;
}

const ACCEPTED_FILE_TYPES = [
  ".csv",
  ".xls",
  ".xlsx",
  "text/csv",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
];

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

export function ImportDialog({ trigger, onUpload }: ImportDialogProps) {
  const [dialogState, setDialogState] = useState<DialogState>("upload");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [importedRows, setImportedRows] = useState<number>(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Get loading, error, and progress states from Zustand store
  const {
    isLoading,
    error: storeError,
    setError: setStoreError,
    progress,
  } = useInfluencerStore();

  const validateFile = (file: File): string | null => {
    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      return "File size exceeds 10MB limit";
    }

    // Check file type
    const isValidType = ACCEPTED_FILE_TYPES.some((type) => {
      if (type.startsWith(".")) {
        return file.name.toLowerCase().endsWith(type);
      }
      return file.type === type;
    });

    if (!isValidType) {
      return "Invalid file type. Please upload CSV or Excel files only";
    }

    return null;
  };

  const handleFileSelect = useCallback(
    (file: File) => {
      const validationError = validateFile(file);
      if (validationError) {
        setLocalError(validationError);
        return;
      }

      setLocalError(null);
      setStoreError(null);
      setSelectedFile(file);
    },
    [setStoreError],
  );

  const handleFileInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      const file = e.dataTransfer.files?.[0];
      if (file) {
        handleFileSelect(file);
      }
    },
    [handleFileSelect],
  );

  const handleUpload = async () => {
    if (!selectedFile) {
      setLocalError("Please select a file first");
      return;
    }

    setDialogState("progress");
    setLocalError(null);
    setStoreError(null);

    try {
      // Call the onUpload callback and WAIT for it to complete
      if (onUpload) {
        await onUpload(selectedFile);
      }

      // Add a small delay to ensure the store has been fully updated
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Now get the actual row count from the store after upload is complete
      const currentData = useInfluencerStore.getState().influencerData;
      setImportedRows(currentData.length);

      // Check if there was an error during processing
      const storeError = useInfluencerStore.getState().error;
      if (storeError) {
        setDialogState("error");
      } else {
        setDialogState("success");
      }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      setDialogState("error");
    }
  };

  const getMissingFieldsCount = () => {
    const currentData = useInfluencerStore.getState().influencerData;
    let missingCount = 0;

    currentData.forEach((row) => {
      Object.values(row).forEach((value) => {
        if (value === "" || value === "N/A") {
          missingCount++;
        }
      });
    });

    return missingCount;
  };

  const resetDialog = () => {
    setDialogState("upload");
    setSelectedFile(null);
    setLocalError(null);
    setStoreError(null);
    setDragActive(false);
    setImportedRows(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Show store error if it exists
  const displayError = storeError || localError;

  const getDialogContent = () => {
    switch (dialogState) {
      case "upload":
        return {
          title: "Import CSV/Excel File",
          description: (
            <div className="flex flex-col gap-6">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xls,.xlsx"
                onChange={handleFileInputChange}
                className="hidden"
              />
              <div
                className={`flex cursor-pointer flex-col items-center gap-3 rounded-[8px] px-5 py-7 transition-colors ${
                  dragActive
                    ? "border-2 border-dashed border-blue-300 bg-blue-50"
                    : selectedFile
                      ? "border-2 border-dashed border-green-300 bg-green-50"
                      : "border-2 border-dashed border-neutral-200 bg-neutral-100 hover:bg-neutral-50"
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <UploadIcon
                  fill={
                    selectedFile
                      ? "#16a34a"
                      : dragActive
                        ? "#2563eb"
                        : "#6B5F50"
                  }
                  width={32}
                  height={32}
                />
                <div className="text-center">
                  {selectedFile ? (
                    <>
                      <p className="text-sm font-bold text-green-700">
                        {selectedFile.name}
                      </p>
                      <p className="text-xs text-green-600">
                        {(selectedFile.size / 1024).toFixed(2)} KB
                      </p>
                      <p className="mt-1 text-xs text-green-600">
                        Click to change file
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="text-xs leading-none font-medium tracking-normal text-neutral-800">
                        <span className="font-bold">Click to import</span> or
                        drag and drop
                      </p>
                      <p className="text-xs leading-none font-medium tracking-normal text-neutral-800">
                        Excel (.xls, .xlsx) or CSV (.csv) files (max. 10MB)
                      </p>
                    </>
                  )}
                </div>
              </div>
              {displayError && (
                <div className="rounded-md bg-red-50 p-3 text-xs text-red-600">
                  {displayError}
                </div>
              )}
              <ul className="list-disc gap-[2px] pl-5 text-xs font-semibold text-blue-600">
                <li>
                  <a
                    href="/templates/influencers.xlsx"
                    download
                    className="cursor-pointer"
                  >
                    Download a sample .xls template
                  </a>
                </li>
                <li>
                  <a href="/templates/influencers.csv" download>
                    Download a sample .csv template
                  </a>
                </li>
              </ul>
            </div>
          ),
          footer: (
            <div className="flex w-full justify-end gap-2">
              <DialogClose asChild className="w-auto text-xs leading-normal">
                <Button type="button" variant="outline" className="w-auto">
                  Cancel
                </Button>
              </DialogClose>
              <Button
                type="button"
                className="w-auto text-xs leading-normal"
                onClick={handleUpload}
                disabled={!selectedFile || isLoading}
              >
                {isLoading ? "Uploading..." : "Upload"}
              </Button>
            </div>
          ),
        };

      case "progress":
        return {
          title: "Import CSV/Excel File",
          description: (
            <div className="flex flex-col gap-6">
              <div className="flex flex-row items-center gap-3 rounded-[8px] bg-blue-50 px-5 py-7">
                <span className="flex items-center justify-center rounded-full bg-blue-600 p-2.5">
                  <UploadIcon
                    fill="#FFFFFF"
                    width={20}
                    height={20}
                    className="p-0.5"
                  />
                </span>
                <div className="flex w-full flex-col gap-[4px]">
                  <div className="flex flex-col gap-[0px]">
                    <p className="text-sm font-bold text-neutral-700">
                      {selectedFile?.name || "Uploading file..."}
                    </p>
                    <p className="text-medium text-[10px] text-neutral-600">
                      {selectedFile
                        ? `${(selectedFile.size / 1024).toFixed(2)} KB`
                        : ""}
                    </p>
                  </div>
                  <Progress value={progress} />
                </div>
              </div>
              <ul className="list-disc gap-[2px] pl-5 text-xs font-semibold text-blue-600">
                <li>
                  <a
                    href="/templates/influencers_with_links.xlsx"
                    download
                    className="cursor-pointer"
                  >
                    Download a sample .xls template
                  </a>
                </li>
                <li>
                  <a href="/templates/influencer-template.csv" download>
                    Download a sample .csv template
                  </a>
                </li>
              </ul>
            </div>
          ),
          footer: (
            <div className="flex w-full justify-end gap-2">
              <DialogClose asChild className="w-auto text-xs leading-normal">
                <Button type="button" variant="outline" className="w-auto">
                  Cancel
                </Button>
              </DialogClose>
            </div>
          ),
        };

      case "success":
        return {
          title: "Import Successful",
          description: (
            <div className="flex flex-col gap-6">
              <div className="flex flex-row items-center gap-3 rounded-[8px] bg-green-50 px-5 py-7">
                <span className="flex items-center justify-center rounded-full bg-green-600 p-2.5">
                  <CheckLineIcon
                    fill="#FFFFFF"
                    width={20}
                    height={20}
                    className="p-0.5"
                  />
                </span>
                <div className="flex w-full flex-col gap-[4px]">
                  <div className="flex flex-col gap-[0px]">
                    <p className="text-sm font-bold text-neutral-700">
                      {selectedFile?.name || "File uploaded successfully"}
                    </p>
                    <p className="text-medium text-[10px] text-neutral-600">
                      {selectedFile
                        ? `${(selectedFile.size / 1024).toFixed(2)} KB`
                        : ""}
                    </p>
                  </div>
                </div>
              </div>
              <ul className="list-disc gap-[2px] pl-5 text-xs font-semibold text-green-600">
                <li>
                  {importedRows} influencer{importedRows !== 1 ? "s" : ""}{" "}
                  imported successfully
                </li>
                <li>All data validated and processed</li>
                <li className="text-brand-500">
                  {getMissingFieldsCount()} cell
                  {getMissingFieldsCount() !== 1 ? "s" : ""} with missing fields
                </li>
              </ul>
            </div>
          ),
          footer: (
            <div className="flex w-full justify-end gap-2">
              <DialogClose asChild className="w-auto text-xs leading-normal">
                <Button type="button" className="w-auto">
                  Done
                </Button>
              </DialogClose>
            </div>
          ),
        };

      case "error":
        return {
          title: "Import Failed",
          description: (
            <div className="flex flex-col gap-6">
              <div className="flex flex-row items-center gap-3 rounded-[8px] bg-red-50 px-5 py-7">
                <span className="flex items-center justify-center rounded-full bg-red-600 p-2.5">
                  <RetryIcon
                    fill="#FFFFFF"
                    width={20}
                    height={20}
                    className="p-0.5"
                  />
                </span>
                <div className="flex w-full flex-col gap-[4px]">
                  <div className="flex flex-col gap-[0px]">
                    <p className="text-sm font-bold text-neutral-700">
                      {selectedFile?.name || "Upload failed"}
                    </p>
                    <p className="text-medium text-[10px] text-red-600">
                      {displayError || "Network error occurred during upload."}
                    </p>
                  </div>
                </div>
              </div>
              <ul className="list-disc gap-[2px] pl-5 text-xs font-semibold text-red-600">
                <li>Invalid file structure detected</li>
                <li>
                  Please ensure your file has the required headers and data
                </li>
                <li>
                  Required headers: Facebook, X (Twitter), Instagram, Reddit,
                  YouTube, TikTok
                </li>
              </ul>
            </div>
          ),
          footer: (
            <div className="flex w-full justify-end gap-2">
              <DialogClose asChild className="w-auto text-xs leading-normal">
                <Button type="button" variant="outline" className="w-auto">
                  Cancel
                </Button>
              </DialogClose>
              <Button
                type="button"
                className="w-auto text-xs leading-normal"
                onClick={resetDialog}
              >
                Try Again
              </Button>
            </div>
          ),
        };
    }
  };

  const content = getDialogContent();

  return (
    <Dialog onOpenChange={(open) => !open && resetDialog()}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent>
        <DialogHeader className="flex flex-col gap-6">
          <DialogTitle>{content.title}</DialogTitle>
          <DialogDescription className="flex flex-col gap-6">
            {content.description}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>{content.footer}</DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
